import { z } from 'zod';
import { ThemeIdSchema } from '../catalog/platformCatalogIds';
import { LayoutBlockSchema, optimizedMediaUrlSchema } from '../content/studio';
import { isPlatformCatalogLocale } from '../locales/platformLocales';
import {
  CustomColorSetsSchema,
  ProjectColorSetIdSchema,
} from '../theme/customColorSet';
import {
  CORNER_ENUM_KEY_VALUES,
  SPACING_ENUM_KEY_VALUES,
  TYPOGRAPHY_ENUM_KEY_VALUES,
} from '../theme/types';

export const ContentDraftPatchSchema = z.object({
  blockId: z.string().uuid(),
  key: z.string().min(1),
  value: z.string(),
  locale: z.string().regex(/^[a-z]{2}(-[a-z0-9]{2,8})*$/),
});

export type ContentDraftPatch = z.infer<typeof ContentDraftPatchSchema>;

/** Media asset URL patch (empty string removes the key). Path-only uploads/defaults allowed. */
export const ContentDraftMediaPatchSchema = z.object({
  blockId: z.string().uuid(),
  key: z.string().min(1),
  value: z.union([optimizedMediaUrlSchema, z.literal('')]),
});

export type ContentDraftMediaPatch = z.infer<typeof ContentDraftMediaPatchSchema>;

/** Replace full draft block matrix (e.g. layer reorder). */
export const ContentDraftBlocksPatchSchema = z.object({
  blocks: z.array(LayoutBlockSchema).min(1),
});

export type ContentDraftBlocksPatch = z.infer<typeof ContentDraftBlocksPatchSchema>;

/** Update theme / spacing on an existing project (workspace theme API). */
export const ProjectThemePatchSchema = z
  .object({
    themeId: ThemeIdSchema.optional(),
    colorsetId: ProjectColorSetIdSchema.optional(),
    spacingEnum: z.enum(SPACING_ENUM_KEY_VALUES).optional(),
    cornerEnum: z.enum(CORNER_ENUM_KEY_VALUES).optional(),
    typographyEnum: z.enum(TYPOGRAPHY_ENUM_KEY_VALUES).optional(),
    /** Replace project custom color-set list (omit = leave unchanged). */
    customColorSets: CustomColorSetsSchema.optional(),
  })
  .refine(
    (v) =>
      v.themeId ??
      v.colorsetId ??
      v.spacingEnum ??
      v.cornerEnum ??
      v.typographyEnum ??
      v.customColorSets,
    {
      message:
        'At least one of themeId, colorsetId, spacingEnum, cornerEnum, typographyEnum, or customColorSets is required.',
    },
  );

export type ProjectThemePatch = z.infer<typeof ProjectThemePatchSchema>;

const reconfigureLocaleKey = z
  .string()
  .regex(/^[a-z]{2}(-[a-z0-9]{2,8})*$/)
  .refine(isPlatformCatalogLocale, { message: 'Locale must be in PLATFORM_LOCALE_CATALOG' });

/** Scopes that can be machine-translated when adding a locale (copy-seed always covers all). */
export const LOCALE_TRANSLATE_SCOPE_VALUES = ['pages', 'blocks', 'menu', 'forms'] as const;
export const LocaleTranslateScopeSchema = z.enum(LOCALE_TRANSLATE_SCOPE_VALUES);
export type LocaleTranslateScope = z.infer<typeof LocaleTranslateScopeSchema>;

export const DEFAULT_LOCALE_TRANSLATE_SCOPES: readonly LocaleTranslateScope[] = [
  ...LOCALE_TRANSLATE_SCOPE_VALUES,
];

/** Machine-translate draft content for a newly added locale (after copy-seed on reconfigure). */
export const TranslateLocaleSchema = z.object({
  targetLocale: reconfigureLocaleKey,
  sourceLocale: reconfigureLocaleKey.optional(),
  overwrite: z.boolean().optional().default(false),
  /** Which scopes to DeepL. Omit = all. Unchecked scopes stay as copy-seed. */
  scopes: z.array(LocaleTranslateScopeSchema).optional(),
});

export type TranslateLocalePayload = z.infer<typeof TranslateLocaleSchema>;

/** Baseline reconfigure (workspace modal) — theme, spacing, and locale policy (v4.1 §3.2.1). */
export const ProjectReconfigureSchema = z
  .object({
    themeId: ThemeIdSchema.optional(),
    colorsetId: ProjectColorSetIdSchema.optional(),
    spacingEnum: z.enum(SPACING_ENUM_KEY_VALUES).optional(),
    cornerEnum: z.enum(CORNER_ENUM_KEY_VALUES).optional(),
    typographyEnum: z.enum(TYPOGRAPHY_ENUM_KEY_VALUES).optional(),
    customColorSets: CustomColorSetsSchema.optional(),
    defaultLocale: reconfigureLocaleKey.optional(),
    supportedLocales: z.array(reconfigureLocaleKey).min(1).optional(),
  })
  .superRefine((patch, ctx) => {
    const hasTheme =
      patch.themeId ??
      patch.colorsetId ??
      patch.spacingEnum ??
      patch.cornerEnum ??
      patch.typographyEnum ??
      patch.customColorSets;
    const hasLocales = patch.defaultLocale ?? patch.supportedLocales;
    if (!hasTheme && !hasLocales) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one reconfigure field is required.',
      });
    }
    if (
      patch.defaultLocale &&
      patch.supportedLocales &&
      !patch.supportedLocales.includes(patch.defaultLocale)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'defaultLocale must be included in supportedLocales',
        path: ['defaultLocale'],
      });
    }
  });

export type ProjectReconfigurePayload = z.infer<typeof ProjectReconfigureSchema>;
